import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router-dom';

import { paths } from 'src/routes/paths';

import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard, PermissionGuard } from 'src/auth/guard';
import { DASHBOARD_ACCESS } from 'src/auth/permission-access';

const OverviewPage = lazy(() => import('src/pages/dashboard/rado/overview'));
const MapUiPage = lazy(() => import('src/pages/dashboard/rado/map-ui'));
const MapPage = lazy(() => import('src/pages/dashboard/rado/map'));
const DevicesPage = lazy(() => import('src/pages/dashboard/rado/devices'));
const DeviceProfilesPage = lazy(() => import('src/pages/dashboard/rado/device-profiles'));
const FleetGroupsPage = lazy(() => import('src/pages/dashboard/rado/fleet-groups'));
const GeofencesPage = lazy(() => import('src/pages/dashboard/rado/geofences'));
const AlertRulesPage = lazy(() => import('src/pages/dashboard/rado/alert-rules'));
const RouteReportPage = lazy(() => import('src/pages/dashboard/rado/route-report'));
const RouteSummaryPage = lazy(() => import('src/pages/dashboard/rado/route-summary'));
const MembersPage = lazy(() => import('src/pages/dashboard/rado/members'));
const OrganizationPage = lazy(() => import('src/pages/dashboard/rado/organization'));
const RolesPage = lazy(() => import('src/pages/dashboard/rado/roles'));
const InvitationsPage = lazy(() => import('src/pages/dashboard/rado/invitations'));
const AuditPage = lazy(() => import('src/pages/dashboard/rado/audit'));
const SystemAccessPage = lazy(() => import('src/pages/dashboard/rado/system-access'));
const SystemUserEditPage = lazy(() => import('src/pages/dashboard/rado/system-user-edit'));
const SystemUserResetPasswordPage = lazy(
  () => import('src/pages/dashboard/rado/system-user-reset-password')
);
const SecuritySettingsPage = lazy(() => import('src/pages/dashboard/rado/security-settings'));
const GeographyCodingsPage = lazy(() => import('src/pages/dashboard/rado/geography-codings'));
const ProfilePage = lazy(() => import('src/pages/dashboard/rado/profile'));
const AboutPage = lazy(() => import('src/pages/dashboard/rado/about'));

const protect = (page, access) => <PermissionGuard access={access}>{page}</PermissionGuard>;

const layoutContent = (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: <AuthGuard>{layoutContent}</AuthGuard>,
    children: [
      { element: <OverviewPage />, index: true },
      { path: 'map-ui', element: protect(<MapUiPage />, DASHBOARD_ACCESS.map) },
      { path: 'map', element: protect(<MapPage />, DASHBOARD_ACCESS.map) },
      { path: 'devices', element: protect(<DevicesPage />, DASHBOARD_ACCESS.devices) },
      {
        path: 'device-profiles',
        element: protect(<DeviceProfilesPage />, DASHBOARD_ACCESS.deviceProfiles),
      },
      {
        path: 'fleet-groups',
        element: protect(<FleetGroupsPage />, DASHBOARD_ACCESS.fleetGroups),
      },
      {
        path: 'geofences',
        element: protect(<GeofencesPage />, DASHBOARD_ACCESS.geofences),
      },
      {
        path: 'alert-rules',
        element: protect(<AlertRulesPage />, DASHBOARD_ACCESS.alertRules),
      },
      {
        path: 'route-report',
        element: protect(<RouteReportPage />, DASHBOARD_ACCESS.routeReport),
      },
      {
        path: 'route-summary',
        element: protect(<RouteSummaryPage />, DASHBOARD_ACCESS.routeReport),
      },
      { path: 'members', element: protect(<MembersPage />, DASHBOARD_ACCESS.members) },
      {
        path: 'organization',
        element: protect(<OrganizationPage />, DASHBOARD_ACCESS.organization),
      },
      { path: 'roles', element: protect(<RolesPage />, DASHBOARD_ACCESS.roles) },
      {
        path: 'invitations',
        element: protect(<InvitationsPage />, DASHBOARD_ACCESS.invitations),
      },
      { path: 'audit', element: protect(<AuditPage />, DASHBOARD_ACCESS.audit) },
      {
        path: 'system-access',
        element: protect(<SystemAccessPage />, DASHBOARD_ACCESS.systemAccess),
      },
      {
        path: 'system-access/edit',
        element: protect(<SystemUserEditPage />, DASHBOARD_ACCESS.systemUserManage),
      },
      {
        path: 'system-access/reset-password',
        element: protect(<SystemUserResetPasswordPage />, DASHBOARD_ACCESS.systemUserManage),
      },
      {
        path: 'security-settings',
        element: protect(<SecuritySettingsPage />, DASHBOARD_ACCESS.securitySettings),
      },
      {
        path: 'geography-codings',
        element: protect(<GeographyCodingsPage />, DASHBOARD_ACCESS.geographyCodings),
      },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'install-app', element: <Navigate replace to={paths.dashboard.root} /> },
    ],
  },
];
